import { computed, effect, inject } from '@angular/core';
import { getState, patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { withLocalStorage, withStorageSync } from '@ngrx-toolkit/core';

import { ScanType } from '@/app/app/constants/scan-type.enum';
import { Configuration } from '@/app/app/interfaces/api/configuration';
import { Deployment } from '@/app/app/interfaces/api/deployment';
import { Tenant } from '@/app/app/interfaces/api/tenant';
import { DeploymentsApi } from '@/app/app/services/api/deployments.api';
import { DevicesApi } from '@/app/app/services/api/devices.api';
import { PreferencesApi } from '@/app/app/services/api/preferences.api';
import { TenantsApi } from '@/app/app/services/api/tenants.api';
import { SignalRService } from '@/app/app/services/communications/signal-r.service';
import { isNotNullOrEmpty } from '@/shared/lib/core/utils/data-validation.util';
import { EnabledFeatures } from '@/shared/lib/interfaces/enabled-features';
import { DataStore } from '@/shared/lib/stores/data.store';
import { NotificationStore } from '@/shared/lib/stores/notification.store';
import { SettingsStore } from '@/shared/lib/stores/settings.store';
import { UserStore } from '@/shared/lib/stores/user.store';

import { storagePrefix } from '../constants/storage.const';

type DeploymentState = {
  selectedTenant: Tenant | null;
  tenantList: Tenant[] | null;
  selectedDeployment: Deployment | null;
  deploymentList: Deployment[] | null;
  deploymentIsLoading: boolean;
  preferences: Configuration | null;
};

const initialState: DeploymentState = {
  selectedTenant: null,
  tenantList: [],
  selectedDeployment: null,
  deploymentList: [],
  deploymentIsLoading: false,
  preferences: null,
};

export const DeploymentStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed((store) => {
    return {
      selectedTenantId: computed(() => {
        return store.selectedTenant()?.id;
      }),
      selectedDeploymentId: computed(() => {
        return store.selectedDeployment()?.id;
      }),
      tenantFeatureList: computed(() => {
        const tenantFeature: EnabledFeatures =
          {
            scan: store.selectedTenant()?.featuresList?.some((s: string) => s.toLowerCase() === 'scan') ?? false,
            connectorCheck: store.selectedTenant()?.featuresList?.some((s: string) => s.toLowerCase() === 'connectorcheck') ?? false,
            history: store.selectedTenant()?.featuresList?.some((s: string) => s.toLowerCase() === 'history') ?? false,
            aux: store.selectedTenant()?.featuresList?.some((s: string) => s.toLowerCase() === 'aux') ?? false,
            vfl: store.selectedTenant()?.featuresList?.some((s: string) => s.toLowerCase() === 'vfl') ?? false,
            monitoring: store.selectedTenant()?.featuresList?.some((s: string) => s.toLowerCase() === 'monitoring') ?? false,
            sorConverter: store.selectedTenant()?.featuresList?.some((s: string) => s.toLowerCase() === 'sorconverter') ?? false,
            realtime: store.selectedTenant()?.featuresList?.some((s: string) => s.toLowerCase() === 'realtime') ?? false,
          };
        return tenantFeature;
      }),
      deploymentFiberList: computed(() => {
        return store.selectedDeployment()?.deploymentPortMappings ?? [];
      }),
      deploymentLaunchFiberBundleArray: computed(() => {
        const launchFiberArray: any[] = [];

        store.selectedDeployment()?.deploymentPortMappings?.forEach((fiber: any) => {
          const launchFiber = Math.ceil(fiber.fiberNumber / 12);
          if (!launchFiberArray.find(f => f === launchFiber)) {
            launchFiberArray.push(Math.ceil(fiber.fiberNumber / 12));
          }
        });

        return launchFiberArray.sort((a, b) => a > b ? 1 : -1);
      }),
      hasDeploymentSelected: computed(() => {
        return isNotNullOrEmpty(store.selectedDeployment());
      }),
    };
  }),
  withComputed((store) => ({
    scanTypeList: computed<any[]>
    (() => {
      const scanTypeEnum: typeof ScanType = ScanType;
      const features: EnabledFeatures = store.tenantFeatureList();
      if (isNotNullOrEmpty(features)) {

        const scanTypeList: any[] = [];
        const enumList = Object.values(ScanType).filter(value => typeof value === 'number');
        for (const option of enumList) {
          if (Number(option) === scanTypeEnum.Scan && features.scan) {
            scanTypeList.push(option);
          }
          if (Number(option) === scanTypeEnum.VisualFaultLocator && features.vfl) {
            scanTypeList.push(option);
          }
          if (Number(option) === scanTypeEnum.Aux && features.aux) {
            scanTypeList.push(option);
          }
          if (Number(option) === scanTypeEnum.MonitoringStart && features.monitoring) {
            scanTypeList.push(option);
          }
          if (Number(option) === scanTypeEnum.ConnectorCheck && features.connectorCheck) {
            scanTypeList.push(option);
          }
        }

        return scanTypeList;
      }
      return [];
    }),
  })),
  // General
  withMethods((store, signalRService = inject(SignalRService), settingsStore = inject(SettingsStore)) => ({
    clear() {
      patchState(store, initialState);
    },
    subscribeSignalR() {
      signalRService.unsubscribeActiveHubs();
      if (isNotNullOrEmpty(store.selectedDeploymentId())) {
        signalRService.subscribeToDeploymentHub(store.selectedDeploymentId()!);
      }
    },
    patchConfiguration(configuration: Configuration) {
      patchState(store, { preferences: configuration });
      // "Auto refresh history" moved to the user settings; the old tenant preference is taken over once
      settingsStore.adoptLegacyAutoRefresh(configuration?.autoOpenLastScanReport);
    },
  })),
  // Deployments
  withMethods((store, deploymentsApi = inject(DeploymentsApi), devicesApi = inject(DevicesApi), preferencesApi = inject(PreferencesApi), dataStore = inject(DataStore), notificationStore = inject(NotificationStore)) => ({
    loadDeploymentList(reload: boolean) {
      patchState(store, { deploymentIsLoading: true });
      if (isNotNullOrEmpty(store.selectedTenantId())) {
        deploymentsApi.getDeploymentDataFromApi().subscribe((deployments: any) => {
          patchState(store, { deploymentList: deployments, deploymentIsLoading: false });
          if (isNotNullOrEmpty(deployments)) {
            this.setDeploymentList(deployments, reload);
          } else {
            patchState(store, { selectedDeployment: null });
          }
        });
      }
    },
    setDeploymentList(deploymentList: Deployment[], reload: boolean) {
      if (deploymentList.length === 1) {
        this.setDeployment(deploymentList[0].id, reload);
      } else if (isNotNullOrEmpty(store.selectedDeploymentId())) {
        this.setDeployment(store.selectedDeploymentId()!, reload);
      }
    },
    setDeployment(deployment: number, reload: boolean) {
      if (isNotNullOrEmpty(deployment)) {
        if (store.selectedDeploymentId() !== deployment || reload) {
          const deploymentFromList = JSON.parse(JSON.stringify(store.deploymentList()?.find((d) => d.id === deployment)));
          if (isNotNullOrEmpty(deploymentFromList) && isNotNullOrEmpty(deploymentFromList.device?.id)) {
            devicesApi.getDeviceConfigurationsExtendedByDeviceId(deploymentFromList.device.id).subscribe((deviceConfiguration: any) => {
              deploymentFromList.deviceSupportedConfigurations = deviceConfiguration;
              patchState(store, { selectedDeployment: deploymentFromList, deploymentIsLoading: false });
            });
            preferencesApi.getPreferencesFromApi(store.selectedTenantId()!).subscribe((preferences) => {
              store.patchConfiguration(preferences);
              dataStore.clearStorage();
              notificationStore.clearStorage();
            });
          }
        }
      } else {
        patchState(store, { selectedDeployment: null, deploymentIsLoading: false });
      }
      store.subscribeSignalR();
    },
  })),
  // Tenants
  withMethods((store, preferencesApi = inject(PreferencesApi), tenantsApi = inject(TenantsApi), dataStore = inject(DataStore), notificationStore = inject(NotificationStore)) => ({
    loadTenantList(reload: boolean) {
      tenantsApi.getTenantByHeader().subscribe((data: any) => {
        if (isNotNullOrEmpty(data)) {
          this.setTenantList(data, reload);
        }
      });
    },
    setTenantList(tenantList: Tenant[], reload: boolean) {
      patchState(store, { tenantList });
      if (tenantList.length === 1) {
        if (isNotNullOrEmpty(tenantList[0].id)) {
          this.setTenant(tenantList[0].id, reload);
        }
      } else if (isNotNullOrEmpty(store.selectedTenantId()) && reload) {
        this.setTenant(store.selectedTenantId()!, reload);
      }
    },
    setTenant(tenant: number, reload: boolean) {
      if (store.selectedTenantId() !== tenant || reload) {
        patchState(store, {
          selectedTenant: store.tenantList()?.find((t) => t.id === tenant),
          selectedDeployment: reload ? store.selectedDeployment() : null,
          deploymentList: [],
          deploymentIsLoading: true,
        });

        preferencesApi.getPreferencesFromApi(store.selectedTenantId()!).subscribe((preferences) => {
          store.patchConfiguration(preferences);
        });
        store.loadDeploymentList(reload);
        dataStore.clearStorage();
        notificationStore.clearStorage();
      }
    },
  })),
  withHooks({
    onInit(store) {
      const signalRService = inject(SignalRService);
      const userStore = inject(UserStore);
      effect(() => {
        const state = getState(store);
        if (isNotNullOrEmpty(state.selectedDeployment?.id) && userStore.isAuthenticated()) {
          signalRService.initialize().then(() => store.subscribeSignalR());
        }
      });
    },
  }),
  withStorageSync(`${storagePrefix}_mobile_deployment`, withLocalStorage()),
);
